import React, { useEffect, useState } from "react";

/**
 * Drop-001 countdown bar.
 * Target date can be passed via prop; defaults to a fixed UTC ISO string.
 * Renders a compact pill showing days / hours / minutes / seconds.
 */
const DEFAULT_TARGET = "2026-08-15T16:00:00Z"; // Drop-001 — Protos 100 applications close

function diff(target) {
  const t = new Date(target).getTime() - Date.now();
  if (Number.isNaN(t) || t <= 0) {
    return { d: 0, h: 0, m: 0, s: 0, done: true };
  }
  const d = Math.floor(t / 86400000);
  const h = Math.floor((t % 86400000) / 3600000);
  const m = Math.floor((t % 3600000) / 60000);
  const s = Math.floor((t % 60000) / 1000);
  return { d, h, m, s, done: false };
}

const pad = (n) => String(Math.max(0, n)).padStart(2, "0");

export default function Countdown({
  target = DEFAULT_TARGET,
  label = "Applications close in",
  testId = "countdown",
  compact = false,
}) {
  const [time, setTime] = useState(() => diff(target));

  useEffect(() => {
    const id = setInterval(() => setTime(diff(target)), 1000);
    return () => clearInterval(id);
  }, [target]);

  if (time.done) {
    return (
      <div
        data-testid={testId}
        className="inline-flex items-center gap-3 px-4 py-2 rounded-full border border-white/10 bg-white/[0.02]"
      >
        <span
          className="inline-block w-1.5 h-1.5 rounded-full"
          style={{
            background: "var(--blue-bright)",
            boxShadow: "0 0 10px var(--blue-bright)",
            animation: "pulse 1.8s ease-in-out infinite",
          }}
        />
        <span className="font-mono text-[0.7rem] uppercase tracking-[0.28em] text-white/80">
          Drop-001 · Live
        </span>
      </div>
    );
  }

  const units = [
    [time.d, "D"],
    [time.h, "H"],
    [time.m, "M"],
    [time.s, "S"],
  ];

  return (
    <div
      data-testid={testId}
      className={`inline-flex items-center ${compact ? "gap-2 px-3 py-1.5" : "gap-3 px-4 py-2"} rounded-full border border-white/10 bg-white/[0.02] backdrop-blur-sm`}
    >
      <span
        className="inline-block w-1.5 h-1.5 rounded-full shrink-0"
        style={{
          background: "var(--blue-bright)",
          boxShadow: "0 0 10px var(--blue-bright)",
          animation: "pulse 1.8s ease-in-out infinite",
        }}
      />
      <span
        className={`font-mono ${compact ? "text-[0.55rem]" : "text-[0.65rem]"} uppercase tracking-[0.28em] text-white/55 whitespace-nowrap`}
      >
        {label}
      </span>
      <span className="flex items-baseline gap-1 tabular-nums font-mono text-white">
        {units.map(([val, suf], i) => (
          <React.Fragment key={suf}>
            <span
              className={compact ? "text-xs font-semibold" : "text-sm font-semibold"}
              data-testid={`${testId}-${suf.toLowerCase()}`}
            >
              {pad(val)}
            </span>
            <span
              className={`${compact ? "text-[0.5rem]" : "text-[0.55rem]"} uppercase tracking-[0.2em] text-white/40`}
            >
              {suf}
            </span>
            {i < units.length - 1 && (
              <span className="text-white/20 px-0.5">:</span>
            )}
          </React.Fragment>
        ))}
      </span>
    </div>
  );
}
